import ActionTextIcon from "@/components/icons/ActionTextIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency, formatLocaleDate } from "@/utils/formatUtils";
import { isDefined, numberOrZero } from "@/utils/utils";
import { Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router";
import { useFilterContracts } from "../../../../common/api/queries";
import { PageSizes, TableSizes } from "../../../../common/constants";
import { UUID } from "../../../../typings/global";
import ContractAmountInfoPopover from "../../../contract/components/ContractAmountInfoPopover";
import ContractStatusTag from "../../../contract/components/tags/ContractStatusTag";
import { ContractView } from "../../../contract/enums";
import { CONTRACT_ROUTE_PATHS } from "../../../contract/paths";
import { ContractList } from "../../../contract/types";

interface Props {
  clientId?: UUID;
}

export const ClientContractsTable = ({ clientId }: Props) => {
  const { t } = useTranslation();

  const [contractPageIndex, setContractPageIndex] = useState(0);

  const { data, isFetching } = useFilterContracts(
    clientId
      ? {
          pageIndex: contractPageIndex,
          pageSize: PageSizes.SMALL,
          report: ContractView.INTERNAL_GENERIC_REPORT,
          clientId,
        }
      : undefined,
  );

  const columns: ColumnsType<ContractList> = [
    {
      key: "effectiveBeginningOrSignDate",
      title: (
        <>
          {t("contract.attrs.effectiveBeginningDateShort")}
          <br />
          {t("contract.attrs.signDate")}
        </>
      ),
      width: 140,
      render: (_, record) => {
        const date = record.effectiveBeginningDate || record.signDate;
        return date ? formatLocaleDate(date) : undefined;
      },
    },
    {
      key: "contractNumbers",
      title: t("contract.attrs.contractNumber"),
      width: 140,
      ellipsis: { showTitle: false },
      render: (_, record) => (
        <>
          {record.contractNumber && (
            <>
              <Ellipsis tooltip={record.contractNumber}>
                <Link className="no-link-color" to={generatePath(CONTRACT_ROUTE_PATHS.detail.to, { id: record.id })}>
                  {record.contractNumber}
                </Link>
              </Ellipsis>
              <br />
            </>
          )}
          {record.secondaryContractNumber && (
            <Ellipsis tooltip={record.secondaryContractNumber}>
              <Link className="no-link-color" to={generatePath(CONTRACT_ROUTE_PATHS.detail.to, { id: record.id })}>
                {record.secondaryContractNumber}
              </Link>
            </Ellipsis>
          )}
        </>
      ),
    },
    {
      key: "institution",
      width: 120,
      title: t("contract.attrs.institution"),
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.institution.name}</Ellipsis>,
    },
    {
      key: "product",
      title: t("contract.attrs.product"),
      width: 160,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.product.name}</Ellipsis>,
    },
    {
      key: "annualPremiumOrApprovedAmount",
      title: <ContractAmountInfoPopover />,
      align: "right",
      width: 120,
      render: (_, record) => {
        const amount = record.annualPremium || record.approvedAmount || record.targetAmount || record.tradeAmount;
        return isDefined(amount) ? formatLocaleCurrency(amount) : undefined;
      },
    },
    {
      key: "status",
      title: t("contract.enums.status._label"),
      align: "center",
      width: 130,
      render: (_, record) =>
        record.status ? <ContractStatusTag status={record.status} style={{ marginRight: 0 }} /> : undefined,
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 90,
      render: (_, record) => (
        <ActionTextIcon
          path={generatePath(CONTRACT_ROUTE_PATHS.detail.to, { id: record.id })}
          icon="eye"
          color="blue"
          text={t("common.show")}
        />
      ),
    },
  ];

  return (
    <Table<ContractList>
      {...tableStandardProps()}
      columns={columns}
      loading={isFetching}
      scroll={{ x: TableSizes.MEDIUM }}
      dataSource={data?.pageData}
      pagination={{
        ...paginationTableProps,
        placement: ["bottomEnd"],
        current: numberOrZero(data?.pageIndex) + 1,
        pageSize: data?.pageSize,
        total: data?.totalElementsCount,
        onChange: (pageIndex) => setContractPageIndex(pageIndex - 1),
      }}
    />
  );
};
